import { useChangePassword } from "@/api/app/profile/useChangePassword";
import InputGroup from "@/components/InputGroup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import toast from "@/lib/toast";
import {
  ChangePassword,
  changePasswordSchema,
} from "@/schema/app/profile/CompanyUser";
import { SpinnerIosRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";

interface ChangePasswordDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function ChangePasswordDialog({ open, setOpen }: ChangePasswordDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const methods = useForm<ChangePassword>({
    resolver: zodResolver(changePasswordSchema),
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    register,
  } = methods;

  const { trigger: changePassword, isMutating } = useChangePassword({
    onSuccess: (data) => {
      setOpen(false);
      toast.success(data.message);
      mutate("/app/assessment");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("change-password")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit((data) => {
              changePassword(data);
            })();
          }}
        >
          <div className="flex flex-col gap-6 px-6 pb-5 pt-4">
            <InputGroup
              label={t("old-password")}
              type="password"
              error={errors.old_password?.message}
              register={register("old_password", {
                required: t("old-password-required"),
              })}
            />
            <InputGroup
              label={t("new-password")}
              type="password"
              error={errors.new_password?.message}
              register={register("new_password", {
                required: t("new-password-required"),
              })}
            />
            <div className="flex flex-col gap-2">
              <div className="text-sm text-red-500">
                {t("change-password-warning")}
              </div>
              <Button
                LoadingComponent={
                  <SpinnerIosRegular className="animate-spin" />
                }
                isLoading={isMutating}
                type="submit"
                disabled={!isDirty}
              >
                {t("change-password")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ChangePasswordDialog;
